import React, { useMemo } from 'react';
import { DateTime, Duration } from 'luxon';

/**
 * Map Moment.js locale preset tokens to Luxon's built-in Intl presets.
 * Luxon already defines these as static properties on DateTime.
 */
const MOMENT_PRESET_TO_LUXON: Record<string, Intl.DateTimeFormatOptions> = {
  L: DateTime.DATE_SHORT,
  LL: DateTime.DATE_FULL,
  LLL: DateTime.DATETIME_MED,
  LLLL: DateTime.DATETIME_MED_WITH_WEEKDAY,
  LT: DateTime.TIME_SIMPLE,
  LTS: DateTime.TIME_WITH_SECONDS,
  l: DateTime.DATE_SHORT,
  ll: DateTime.DATE_MED,
  lll: DateTime.DATETIME_MED,
  llll: DateTime.DATETIME_MED_WITH_WEEKDAY,
};

/**
 * Single-pass tokenizer that converts a moment.js format string to a Luxon format string.
 * Handles escaped text in square brackets (moment) -> single quotes (Luxon).
 */
const TOKEN_REGEX =
  /YYYY|YY|MMMM|MMM|MM|M|dddd|ddd|dd|d|DD|Do|D|HH|H|hh|h|mm|m|ss|s|A|a|ZZ|Z|z|\[([^\]]{0,250})\]|./g;

const MOMENT_TO_LUXON_TOKENS: Record<string, string> = {
  YYYY: 'yyyy',
  YY: 'yy',
  MMMM: 'MMMM',
  MMM: 'MMM',
  MM: 'MM',
  M: 'M',
  DD: 'dd',
  Do: 'd',
  D: 'd',
  dddd: 'cccc',
  ddd: 'ccc',
  dd: 'cc',
  d: 'c',
  HH: 'HH',
  H: 'H',
  hh: 'hh',
  h: 'h',
  mm: 'mm',
  m: 'm',
  ss: 'ss',
  s: 's',
  A: 'a',
  a: 'a',
  ZZ: 'ZZZ',
  Z: 'ZZ',
  z: 'ZZZZ',
};

export function momentFormatToLuxon(momentFmt: string): string {
  return momentFmt.replace(TOKEN_REGEX, (match, escaped) => {
    if (escaped !== undefined) return `'${escaped}'`;
    return MOMENT_TO_LUXON_TOKENS[match] ?? match;
  });
}

/**
 * Approximate calendar-relative formatting to match moment's `calendar()` output.
 * "Today at 3:00 PM", "Yesterday at ...", "Last Monday at ...", etc.
 */
function calendarFormat(dt: DateTime, locale?: string): string {
  const now = DateTime.now();
  const daysDiff = Math.round(
    dt.startOf('day').diff(now.startOf('day'), 'days').days,
  );
  const opts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  };
  const timeStr = dt.toLocaleString(opts, { locale });

  if (daysDiff === 0) return `Today at ${timeStr}`;
  if (daysDiff === -1) return `Yesterday at ${timeStr}`;
  if (daysDiff === 1) return `Tomorrow at ${timeStr}`;
  if (daysDiff > -7 && daysDiff < 0)
    return `Last ${dt.toFormat('cccc')} at ${timeStr}`;
  if (daysDiff > 0 && daysDiff < 7)
    return `${dt.toFormat('cccc')} at ${timeStr}`;
  return dt.toLocaleString(DateTime.DATE_SHORT, { locale });
}

/**
 * Apply moment-compatible thresholds for unit selection.
 * e.g. 45-89 min → "1 hour", 90-1319 min → "X hours", etc.
 */
function durationWithMomentThresholds(dt: DateTime, locale?: string): Duration {
  const diffMs = Math.abs(DateTime.now().toMillis() - dt.toMillis());
  const seconds = diffMs / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;
  const days = hours / 24;

  let dur: Duration;
  if (seconds < 45) {
    dur = Duration.fromObject({ seconds: Math.round(seconds) });
  } else if (seconds < 90) {
    dur = Duration.fromObject({ minutes: 1 });
  } else if (minutes < 45) {
    dur = Duration.fromObject({ minutes: Math.round(minutes) });
  } else if (minutes < 90) {
    dur = Duration.fromObject({ hours: 1 });
  } else if (hours < 22) {
    dur = Duration.fromObject({ hours: Math.round(hours) });
  } else if (hours < 36) {
    dur = Duration.fromObject({ days: 1 });
  } else if (days < 26) {
    dur = Duration.fromObject({ days: Math.round(days) });
  } else if (days < 45) {
    dur = Duration.fromObject({ months: 1 });
  } else if (days < 345) {
    dur = Duration.fromObject({ months: Math.round(days / 30.44) });
  } else {
    dur = Duration.fromObject({ years: Math.round(days / 365.25) });
  }

  if (locale) dur = dur.reconfigure({ locale });
  return dur;
}

/**
 * Relative duration string without directional suffix ("5 minutes" instead of "5 minutes ago").
 * Uses moment-compatible thresholds for unit selection.
 */
function relativeWithoutSuffix(dt: DateTime, locale?: string): string {
  return durationWithMomentThresholds(dt, locale).toHuman();
}

/**
 * Relative duration string with directional suffix ("in 5 minutes" or "5 minutes ago").
 * Uses moment-compatible thresholds for unit selection.
 */
function relativeWithSuffix(dt: DateTime, locale?: string): string {
  const now = DateTime.now();
  const isFuture = dt > now;
  const label = durationWithMomentThresholds(dt, locale).toHuman();
  return isFuture ? `in ${label}` : `${label} ago`;
}

function parseDateTimeInput(value: string | number): DateTime {
  if (typeof value === 'number') return DateTime.fromMillis(value);

  const str = String(value);
  const fromISO = DateTime.fromISO(str);
  if (fromISO.isValid) return fromISO;

  const fromRFC = DateTime.fromRFC2822(str);
  if (fromRFC.isValid) return fromRFC;

  const fromHTTP = DateTime.fromHTTP(str);
  if (fromHTTP.isValid) return fromHTTP;

  return DateTime.fromJSDate(new Date(str));
}

interface FormattedDateTimeProps {
  children?: string | number;
  /** Moment-style format string (e.g. "YYYY-MM-DD") or locale preset (e.g. "LL") */
  format?: string;
  locale?: string;
  /** IANA timezone (e.g. "America/New_York") */
  tz?: string;
  /** Show relative time ("5 minutes ago") */
  fromNow?: boolean;
  /** When combined with fromNow, drops the suffix ("5 minutes" instead of "5 minutes ago") */
  ago?: boolean;
  /** Calendar-relative display ("Today at 3 PM", "Yesterday at ...") */
  calendar?: boolean;
  /** HTML title attribute; true uses the ISO string, string uses the literal value */
  title?: string | boolean;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Drop-in replacement for react-moment's `<Moment>` component, powered by Luxon.
 *
 * Supports the prop combinations used in the codebase:
 * - Default (children only): locale-aware medium datetime
 * - format: custom or locale-preset format string
 * - locale + tz: timezone-aware locale display
 * - fromNow / fromNow+ago: relative time
 * - calendar: calendar-relative display
 * - title: HTML title attribute
 */
const FormattedDateTime: React.FC<FormattedDateTimeProps> = ({
  children,
  format,
  locale,
  tz,
  fromNow,
  ago,
  calendar,
  title,
  style,
  className,
}) => {
  const formatted = useMemo(() => {
    if (children == null) return null;

    let dt = parseDateTimeInput(children);
    if (!dt.isValid) return String(children);

    if (tz) dt = dt.setZone(tz);
    if (locale) dt = dt.setLocale(locale);

    if (fromNow) {
      if (ago) return relativeWithoutSuffix(dt, locale);
      return relativeWithSuffix(dt, locale);
    }

    if (calendar) return calendarFormat(dt, locale);

    if (format) {
      const preset = MOMENT_PRESET_TO_LUXON[format];
      if (preset) return dt.toLocaleString(preset);
      return dt.toFormat(momentFormatToLuxon(format));
    }

    return dt.toLocaleString(DateTime.DATETIME_MED);
  }, [children, format, locale, tz, fromNow, ago, calendar]);

  if (formatted == null) return null;

  let titleAttr: string | undefined;
  if (typeof title === 'string') {
    titleAttr = title;
  } else if (title === true && children != null) {
    const dt = parseDateTimeInput(children);
    titleAttr = dt.isValid ? dt.toISO() ?? undefined : undefined;
  }

  return (
    <span title={titleAttr} style={style} className={className}>
      {formatted}
    </span>
  );
};

export default FormattedDateTime;
